import { BoundSelectCardGroupField, FormSectionLayout } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useRouteParams } from "src/hooks/useRouteParams";
import { GuidedChangeEventConfigType } from "src/routes/projects/change-events/guided/utils";
import { ProjectParams } from "src/routes/routesDef";
import { queryResult } from "src/utils";
import {
  SelectConfigurationStep_StructureFragment,
  useSelectConfigurationStepQuery,
} from "./1.SelectConfigurationStep.generated";
import { ApplyConfigurationFormInput } from "./applyConfigurationForm";
import { ConfigurationChangeSection } from "./components/ConfigurationChangeSection";
import { clearSelectedConfiguration } from "./utils";

type SelectConfigurationStepProps = { formState: ObjectState<ApplyConfigurationFormInput> };

export function SelectConfigurationStep(props: SelectConfigurationStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useSelectConfigurationStepQuery({ variables: { structureId: projectId } });
  return queryResult(query, ({ structure }) => (
    <SelectConfigurationStepView formState={formState} structure={structure} />
  ));
}

type SelectConfigurationStepViewProps = SelectConfigurationStepProps & {
  structure: SelectConfigurationStep_StructureFragment;
};

function SelectConfigurationStepView(props: SelectConfigurationStepViewProps) {
  const { formState, structure } = props;

  return (
    <FormSectionLayout
      title="Select the Configuration to Apply"
      description="Choose an existing Product Offering or Checkout configuration to replace the current configuration on this structure."
      sections={[
        {
          title: "New Configuration Type",
          fields: (
            <BoundSelectCardGroupField
              field={formState.selectedConfigType}
              label="Type"
              labelStyle="hidden"
              view="list"
              options={[
                {
                  value: GuidedChangeEventConfigType.CheckoutConfig,
                  label: "Checkout Config",
                  tooltip: "Only reserved/contracted configurations for this address.",
                  description:
                    "Apply a homeowner-approved configuration created specifically for this structure. Includes project- specific pricing.",
                },
                {
                  value: GuidedChangeEventConfigType.ProductOfferingConfig,
                  label: "Product Offering Config",
                  tooltip: "Only configurations from the applicable Product Offering.",
                  description:
                    "Apply a standard configuration from the current product offering. Applies the latest catalog definition and pricing.",
                },
              ]}
              onChange={(value) => {
                formState.selectedConfigType.set(value);
                // Changing the type clears any previously selected config
                clearSelectedConfiguration(formState);
              }}
            />
          ),
        },
        {
          title: "Configuration Change",
          fields: <ConfigurationChangeSection formState={formState} structure={structure} />,
        },
      ]}
    />
  );
}
